import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Text } from '@/shared/ui';
import { ExternalLinkIcon } from '@/shared/ui/radix-icons';
import { FormattedDate } from '@/shared/ui/components/basic/formatted-date';
import { DealsService } from '@/services/requests/housien-services/DealsService';
import { DealStageBadge } from '@/features/deals/components/DealStageBadge';
import { SectionCard } from './VisitDetailPrimitives';

interface VisitAttendanceCardProps {
  agencyId: string;
  dealId?: string | null;
  className?: string;
}

/**
 * Links the visit back to the attendance it belongs to.
 *
 * A visit is one step of an ongoing attendance, and the modal was a dead end:
 * the broker could read the visit but not reach the negotiation it came from.
 * Renders nothing when the visit carries no `deal_id` -- an unlinked visit has
 * no attendance to open, and an empty card would only pose the question.
 *
 * @param props.agencyId - Agency owning the visit, for the detail route.
 * @param props.dealId - The attendance this visit belongs to, when there is one.
 * @param props.className - Extra classes on the card surface.
 */
export function VisitAttendanceCard({ agencyId, dealId, className }: VisitAttendanceCardProps) {
  const { t } = useTranslation('@pages/agencies');
  const navigate = useNavigate();

  const { data: deal, isLoading } = useQuery({
    queryKey: ['visit-attendance', agencyId, dealId],
    queryFn: () => DealsService.getDeal(agencyId, dealId!),
    enabled: Boolean(agencyId && dealId),
  });

  if (!dealId) return null;

  return (
    <SectionCard
      title={t('visits.detail.attendance', 'Atendimento')}
      titleCase="sentence"
      className={className}
    >
      <div
        className="cursor-pointer group"
        onClick={() => navigate(`/app/agencies/${agencyId}/deals/${dealId}`)}
        data-testid="visit-attendance-link"
      >
        {/* The badge is the attendance's funnel stage, not this visit's status --
            both can read "agendada" in the same modal, so the row is labelled.
            Without the label the broker cannot tell which of the two it names. */}
        <div className="flex items-center justify-between gap-[var(--space-2)]">
          <div className="flex min-w-0 items-center gap-[var(--space-2)]">
            <Text className="shrink-0 text-[length:var(--text-meta)] text-(--text-tertiary)">
              {t('visits.detail.attendanceStage', 'Etapa')}
            </Text>
            {deal ? (
              <DealStageBadge stage={deal.stage} />
            ) : (
              <Text className="text-[length:var(--text-meta)] text-(--text-tertiary)">
                {isLoading ? t('visits.detail.attendanceLoading', 'Carregando…') : '—'}
              </Text>
            )}
          </div>
          <ExternalLinkIcon
            width={12}
            height={12}
            className="shrink-0 text-(--text-tertiary) opacity-60 group-hover:opacity-100"
          />
        </div>
        {deal?.created_at && (
          <Text className="mt-[var(--space-2)] text-[length:var(--text-meta)] text-(--text-secondary) group-hover:underline">
            {t('visits.detail.attendanceOpenedOn', 'Aberto em')}{' '}
            <FormattedDate value={deal.created_at} />
          </Text>
        )}
      </div>
    </SectionCard>
  );
}
